/** API client for /api/v1/trading/personas endpoints. */

import type { PersonaMeta } from '../types/persona';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8001';

function getToken(): string | null {
  return localStorage.getItem('token');
}

async function request<T>(endpoint: string): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${API_URL}${endpoint}`, { headers });

  if (response.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }

  return response.json();
}

/** Raw response shape from the backend (snake_case). */
interface PersonaResponseRaw {
  slug: string;
  name: string;
  display_title: string;
  avatar_key: string | null;
  avatar_type: string;
  voice_provider: string;
  domains: string[];
  is_active: boolean;
  sort_order: number;
}

function mapPersona(raw: PersonaResponseRaw): PersonaMeta {
  return {
    slug: raw.slug,
    name: raw.name,
    displayTitle: raw.display_title,
    avatarKey: raw.avatar_key,
    domains: raw.domains,
    isActive: raw.is_active,
    sortOrder: raw.sort_order,
  };
}

export interface PersonaAdminPayload {
  slug?: string;
  name?: string;
  display_title?: string;
  avatar_type?: string;
  avatar_key?: string | null;
  voice_provider?: string;
  voice_id?: string | null;
  system_prompt?: string;
  tools_allowed?: string[];
  domains?: string[];
  context_strategy?: Record<string, unknown>;
  is_active?: boolean;
  sort_order?: number;
}

async function write<T>(
  method: 'POST' | 'PUT' | 'DELETE',
  endpoint: string,
  body?: unknown,
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const response = await fetch(`${API_URL}${endpoint}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (response.status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Session expired');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Request failed' }));
    throw new Error(error.detail || 'Request failed');
  }

  if (response.status === 204) return undefined as unknown as T;
  return response.json();
}

export const personaApi = {
  /** Fetch all active personas ordered by sort_order. */
  listPersonas: async (): Promise<PersonaMeta[]> => {
    const raw = await request<PersonaResponseRaw[]>('/api/v1/trading/personas');
    return raw.map(mapPersona);
  },

  /** Fetch all personas including inactive (admin only). */
  listAllPersonas: async (): Promise<PersonaResponseRaw[]> => {
    return request<PersonaResponseRaw[]>('/api/v1/trading/personas?include_inactive=true');
  },

  /** Fetch a single persona by slug. */
  getPersona: async (slug: string): Promise<PersonaMeta> => {
    const raw = await request<PersonaResponseRaw>(`/api/v1/trading/personas/${slug}`);
    return mapPersona(raw);
  },

  /** Create a new persona (admin only). */
  createPersona: async (payload: PersonaAdminPayload): Promise<PersonaResponseRaw> => {
    return write<PersonaResponseRaw>('POST', '/api/v1/trading/personas', payload);
  },

  /** Update a persona (admin only). */
  updatePersona: async (slug: string, payload: PersonaAdminPayload): Promise<PersonaResponseRaw> => {
    return write<PersonaResponseRaw>('PUT', `/api/v1/trading/personas/${slug}`, payload);
  },

  /** Soft-delete a persona (admin only). */
  deletePersona: async (slug: string): Promise<void> => {
    return write<void>('DELETE', `/api/v1/trading/personas/${slug}`);
  },

  /** Reorder personas (admin only). */
  reorderPersonas: async (order: string[]): Promise<void> => {
    return write<void>('POST', '/api/v1/trading/personas/reorder', { order });
  },
};
